import React, { useState, useCallback } from 'react';
import { Speaker } from '../../types/conference';

interface MultiSpeakerSelectorProps {
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  onSpeakersChange: (speakers: Speaker[]) => void;
  maxSpeakers?: number;
}

const MultiSpeakerSelector: React.FC<MultiSpeakerSelectorProps> = ({
  speakers,
  selectedSpeakers,
  onSpeakersChange,
  maxSpeakers = 10
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const availableSpeakers = speakers.filter(
    speaker => !selectedSpeakers.some(selected => selected.id === speaker.id)
  );

  const filteredAvailable = availableSpeakers.filter(speaker =>
    speaker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    speaker.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addSpeaker = useCallback((speaker: Speaker) => {
    if (selectedSpeakers.length < maxSpeakers) {
      const newSpeaker = { ...speaker, display_order: selectedSpeakers.length };
      onSpeakersChange([...selectedSpeakers, newSpeaker]);
    }
  }, [selectedSpeakers, onSpeakersChange, maxSpeakers]);

  const removeSpeaker = useCallback((speakerId: string) => {
    const updated = selectedSpeakers
      .filter(s => s.id !== speakerId)
      .map((s, index) => ({ ...s, display_order: index }));
    onSpeakersChange(updated);
  }, [selectedSpeakers, onSpeakersChange]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newSpeakers = [...selectedSpeakers];
    const draggedSpeaker = newSpeakers[draggedIndex];
    
    // Remove dragged speaker
    newSpeakers.splice(draggedIndex, 1);
    
    // Insert at new position
    newSpeakers.splice(dropIndex, 0, draggedSpeaker);
    
    // Update display_order
    const reordered = newSpeakers.map((speaker, index) => ({
      ...speaker,
      display_order: index
    }));
    
    onSpeakersChange(reordered);
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Selected Speakers ({selectedSpeakers.length}/{maxSpeakers})
        </label>
        
        {selectedSpeakers.length > 0 ? (
          <div className="space-y-2">
            {selectedSpeakers.map((speaker, index) => (
              <div
                key={speaker.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md cursor-move hover:bg-blue-100 ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-blue-800">#{index + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{speaker.name}</p>
                    <p className="text-xs text-gray-500">
                      {speaker.title} {speaker.company && `at ${speaker.company}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeSpeaker(speaker.id)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500 border-2 border-dashed border-gray-300 rounded-md">
            No speakers selected. Add speakers from the list below.
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Available Speakers
        </label>
        
        <input
          type="text"
          placeholder="Search speakers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full mb-3 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        
        <div className="max-h-48 overflow-y-auto space-y-2">
          {filteredAvailable.map((speaker) => (
            <div
              key={speaker.id}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{speaker.name}</p>
                <p className="text-xs text-gray-500">
                  {speaker.title} {speaker.company && `at ${speaker.company}`}
                </p>
              </div>
              <button
                onClick={() => addSpeaker(speaker)}
                disabled={selectedSpeakers.length >= maxSpeakers}
                className="text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          ))}
          
          {filteredAvailable.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              {searchTerm ? 'No speakers found matching your search.' : 'No available speakers.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiSpeakerSelector;
